import { makeProviderKey, type ProviderKey } from '@littlebird/integrations';

import IMessageLogo from '@/assets/images/channels/imessage.png';
import WhatsAppLogo from '@/assets/images/channels/whatsapp.png';
import { YStack } from '@/components/ui/layout/layout';
import { ScrollMask } from '@/components/ui/ScrollMask';
import { AXON_MANIFEST_TOOLKIT_IDS } from '@/features/integration-management/integration-management.adapters';
import { gridItemKey } from '@/features/integrations/add-integrations-modal';
import { type GridItem } from '@/features/integrations/add-integrations.hooks';
import { MESSAGING_CHANNEL_LABELS } from '@/features/msg-channels/common/msg-channels.types';
import { IntegrationIcon } from '@/integrations/integration-icon';

const CHANNEL_ICON_SRC = {
  imessage: IMessageLogo,
  whatsapp_business: WhatsAppLogo,
} as const;

const itemDetails = (
  item: GridItem
): { name: string; iconUrl?: string | null; iconKey: string } => {
  switch (item.type) {
    case 'integration':
      return {
        name: item.integration.info.display_name,
        iconUrl: item.integration.info.logo_url,
        iconKey: item.integration.info.toolkit_name,
      };
    case 'mcp_server':
      return {
        name: item.server.name,
        iconUrl: item.iconUrl,
        iconKey: item.server.slug,
      };
    case 'local_mcp_template':
      return {
        name: item.entry.name,
        iconUrl: item.entry.icon_url,
        iconKey: item.entry.id,
      };
    case 'bundled_mcp_template':
      return {
        name: item.template.name,
        iconKey: item.template.id,
      };
    case 'channel':
      return {
        name: MESSAGING_CHANNEL_LABELS[item.channelType],
        iconUrl: CHANNEL_ICON_SRC[item.channelType],
        iconKey: item.channelType,
      };
    case 'mcp_catalog':
      return {
        name: item.entry.name,
        iconUrl: item.entry.icon_url,
        iconKey: item.entry.id,
      };
    case 'guided_custom_mcp':
      return {
        name: item.offer.name,
        iconKey: item.offer.id,
      };
  }
};

const providerKey = (item: GridItem): ProviderKey | undefined => {
  switch (item.type) {
    case 'integration': {
      const toolkit = item.integration.info.toolkit_name;
      if (!item.integration.info.on_device) {
        return makeProviderKey('composio', toolkit);
      }
      const manifestToolkit =
        AXON_MANIFEST_TOOLKIT_IDS[
          toolkit as keyof typeof AXON_MANIFEST_TOOLKIT_IDS
        ];
      return manifestToolkit
        ? makeProviderKey('device', manifestToolkit)
        : undefined;
    }
    case 'mcp_server':
      return makeProviderKey('mcp', item.server.slug);
    case 'local_mcp_template':
    case 'bundled_mcp_template':
      return item.installedServer
        ? makeProviderKey('device', item.installedServer.slug)
        : undefined;
    case 'channel':
      return makeProviderKey('channel', item.channelType);
    case 'mcp_catalog':
    case 'guided_custom_mcp':
      return undefined;
  }
};

const InstalledIcon = ({
  item,
  openSettings,
}: {
  item: GridItem;
  openSettings: (providerKey?: ProviderKey) => void;
}) => {
  const { name, iconUrl, iconKey } = itemDetails(item);

  return (
    <button
      type="button"
      aria-label={`Manage ${name}`}
      title={name}
      onClick={() => openSettings(providerKey(item))}
      className="flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border bg-surface transition-colors hover:bg-surface-hover hover:opacity-80 focus-ring"
    >
      <IntegrationIcon
        iconKey={iconKey}
        iconUrl={iconUrl}
        name={name}
        className="size-full"
      />
    </button>
  );
};

export const InstalledIntegrations = ({
  items,
  openSettings,
}: {
  items: GridItem[];
  openSettings: (providerKey?: ProviderKey) => void;
}) => (
  <YStack className="gap-4">
    <h3 className="text-sm font-medium text-foreground-muted">Connected</h3>
    <ScrollMask
      direction="horizontal"
      showEndFade
      fadeSize={24}
      className="flex gap-3 overflow-x-auto pb-1 scrollbar-hide"
    >
      {items.map((item) => (
        <InstalledIcon
          key={gridItemKey(item)}
          item={item}
          openSettings={openSettings}
        />
      ))}
    </ScrollMask>
  </YStack>
);
